import { useEffect, useState } from 'react';
import Moment from 'react-moment';
import Loading from '../../common/loading';
import Table from '../../common/table';
import l from '../../../lang';
import './styles.scss';
import { Urls } from '../../../config/urls';
import { createDate, getMappedCountryCode } from '../../../utils/index';
import TopDashboardSection from '../../common/top-dashboard-section';
import ButtonMore from '../../common/button-more';
import Card from '../../common/card';
import CardDescription from '../../common/card-description';
import { Empty, EmptyNoFound } from './empty';
import PayoutsTransactionFilter from './filter';
import { useMediaQuery } from '@mui/material';
import ExportPayouts from './export';
import { usePayouts } from '../../../store/payouts';
import { Filters } from '../../../store/interfaces';
import AlertMessages from '../alert-messages';
import StatusIcon from '../../common/status-icon';
import PayoutDrawer from './drawer';
import { Payout } from '../../../store/payouts/interfaces';
import { Button } from '../../common/button';
import { useHistory } from 'react-router-dom';
import { Routes } from '../../../config/routes';
import { useDashboard } from '../../../store/dashboard';

const Payouts = () => {
  const history = useHistory();
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const { loading, error, allPayouts, getPayouts, startCreatePayout } = usePayouts();
  const { merchantMe } = useDashboard();

  const initFilters = { search_by: '', status: '', country: '', from: '', to: '' };
  const [filter, setFilter] = useState<Filters>(initFilters);
  const [drawerPayout, setDrawerPayout] = useState<Payout | null>(null);

  useEffect(() => {
    getPayouts(0, filter);
  }, [filter]);

  const hasFilter = Object.values(filter).some(value => value !== '');
  const hasPayouts = allPayouts && allPayouts?.data?.length > 0;

  const EmptyBody = () => {
    if (!error && !hasPayouts && !hasFilter) {
      return <Empty />;
    }
    if (error || !hasPayouts) {
      return <EmptyNoFound />;
    }
    return null;
  };

  return (
    <div className="transactions">
      <TopDashboardSection title={l('menu.item.PAYOUTS')} subtitle={l('menu.item.PAYOUTS.subtitle')} />
      <AlertMessages />
      <Card>
        <CardDescription>
          <div className="transactions_header">
            <span>{l('menu.item.PAYOUTS.desc')}</span>
            <div>
              <Button
                variant="contained"
                color="primary"
                textTransformNone
                onClick={() => {
                  startCreatePayout();
                  history.push(Routes.PAYOUTS_STEP_PAYOUT_DATA);
                }}
              >
                + {l('payouts.newPayout')}
              </Button>
            </div>
          </div>
        </CardDescription>
        <div className="transactions__content">
          <div className="transactions__filter_wrapper">
            <PayoutsTransactionFilter
              loading={loading}
              appliedFilters={filter}
              clearFilters={() => setFilter(initFilters)}
              changeFilters={filters => setFilter(filters)}
              exportButton={<ExportPayouts />}
            />
          </div>
          {loading && (
            <div className="transactions__loading">
              <Loading />
            </div>
          )}
          {!loading && (
            <div className="transactions__table">
              <Table
                withBtnMenu
                emptyBody={<EmptyBody />}
                headers={[
                  <span className="transactions__column__left">{l('payouts.creationDate')}</span>,
                  <span className="transactions__column__left">{l('payouts.completedDate')}</span>,
                  <span className="transactions__column__left">{l('payouts.id')}</span>,
                  <span className="transactions__column__left">{l('payouts.country')}</span>,
                  <span className="transactions__column__left">{l('payouts.bankName')}</span>,
                  <span className="transactions__column__left transactions__title">{l('payouts.paidAmount')}</span>,
                  <span className="transactions__column__left transactions__title">{l('payouts.balanceAmount')}</span>,
                  <span>{l('payouts.status')}</span>,
                ]}
                body={
                  (hasPayouts &&
                    !error &&
                    allPayouts?.data?.map(payout => {
                      return [
                        <span className="transactions__column__left">
                          <Moment format="DD/MM/YYYY">{createDate(payout.created_at)}</Moment>
                        </span>,
                        <span className="transactions__column__left">
                          {payout.completed_at ? (
                            <Moment format="DD/MM/YYYY">{createDate(payout.completed_at)}</Moment>
                          ) : (
                            '-'
                          )}
                        </span>,
                        <span className="transactions__column__left transactions__column__invoice">
                          <span>{payout.payout_id}</span>
                        </span>,
                        <span className="transactions__column__left transactions__country">
                          <img
                            alt=""
                            className="transactions__country__flag country-flag--rounded"
                            src={`${Urls.roundedFlags.replace(':code', getMappedCountryCode(payout.country))}`}
                          />
                          &nbsp;
                          <span>{payout.country}</span>
                        </span>,
                        <span className="transactions__column__left">
                          <span>{payout.bank_name ?? '-'}</span>
                        </span>,
                        <span className="transactions__column__left">
                          {payout.currency_to_pay} {payout.amount}
                        </span>,
                        <span className="transactions__column__left">
                          {merchantMe?.functionalCurrency} {payout.balance_total_amount}
                        </span>,
                        <div>
                          <StatusIcon status={payout.status} />
                        </div>,
                        <div>
                          <ButtonMore onClick={() => setDrawerPayout(payout)} />
                        </div>,
                      ];
                    })) ||
                  null
                }
                rowsPerPageOptions={[allPayouts?.size]}
                rowsLength={allPayouts?.totalElements}
                rowsPerPage={allPayouts?.size}
                page={allPayouts?.page}
                handleChangePage={(event, newPage) => {
                  getPayouts(newPage, filter);
                }}
              />
            </div>
          )}
        </div>
      </Card>
      {drawerPayout && <PayoutDrawer data={drawerPayout} onClose={() => setDrawerPayout(null)} />}
    </div>
  );
};

export default Payouts;
